import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import {
  dominant,
  FA_COLOR,
  vectorAt,
  type EmotionKey,
} from '@/components/researcher/liveDemo/constants'
import { primeFirstFrame } from '@/utils/primeFirstFrame'

/**
 * Picture-in-picture of the respondent's face recording, pinned to a corner of
 * the stimulus stage and kept in sync with the panel's playback clock.
 *
 * The face capture is one continuous video covering the whole study, while the
 * playhead (`currentMs`) is stimulus-local. `offsetMs` is where the current
 * stimulus begins inside that recording, so the face video tracks
 * `offsetMs + currentMs`. We let it play in real time and only seek when it
 * drifts, mirroring how `StimulusStage` syncs the stimulus video.
 *
 * `src` may be a blob URL (researcher, authenticated fetch) or a direct stream
 * URL (public respondent endpoint). It is always muted — the stimulus carries
 * the audio; two audio tracks would echo.
 *
 * When `series` + `gridMs` + `labels` are supplied, a caption under the pip
 * shows the dominant emotion + its percentage at the current playhead, so the
 * face preview reads as "what is being felt right now".
 *
 * With `onClick` the pip becomes an interactive tile (FaceTime-style): clicking
 * it promotes the face to the main view (handled by the parent, e.g.
 * `SwappableStage`). It then sits above the stage's click-to-play scrim so the
 * swap works while paused too.
 */
export default function FacePip({
  src,
  currentMs,
  offsetMs = 0,
  playing,
  corner = 'top-right',
  series,
  gridMs,
  labels,
  onClick,
  onAspect,
}: {
  src: string | null
  currentMs: number
  offsetMs?: number
  playing: boolean
  corner?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left'
  /** Per-emotion values 0..1 sampled on `gridMs`; enables the emotion caption. */
  series?: Record<EmotionKey, (number | null)[]> | null
  /** Grid timestamps in ms, ascending. */
  gridMs?: number[]
  /** Display label per emotion (i18n / locale-specific). */
  labels?: Record<EmotionKey, string>
  /** Makes the pip clickable (e.g. to enlarge the face view). */
  onClick?: () => void
  /** Reports the recording's natural aspect ratio (w/h) once metadata loads, so
   *  the parent can pick a layout (e.g. side-by-side for portrait captures). */
  onAspect?: (aspect: number) => void
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [failed, setFailed] = useState(false)
  const [ready, setReady] = useState(false)
  // Natural aspect ratio (w/h) of the face recording. Portrait (mobile) and
  // landscape (desktop) captures differ, so we measure rather than assume.
  // Defaults to 4:3 until the first metadata arrives.
  const [aspect, setAspect] = useState(4 / 3)
  // Latest `playing` for the async first-frame kick (see prime()).
  const playingRef = useRef(playing)
  playingRef.current = playing
  // The first-frame paint runs once per source.
  const primedRef = useRef(false)

  // A new source gets a fresh chance to load.
  useEffect(() => {
    setFailed(false)
    setReady(false)
    setAspect(4 / 3)
    primedRef.current = false
  }, [src])

  // Reflect play/pause onto the <video>.
  useEffect(() => {
    const v = videoRef.current
    if (!v || !src) return
    if (playing) v.play().catch(() => {})
    else v.pause()
  }, [playing, src])

  // The playhead the face video should sit at, held in a ref so the async media
  // callbacks below always read the freshest target (the recording covers the
  // whole study; `offsetMs + currentMs` is the stimulus-local position).
  const targetSecRef = useRef(0)
  targetSecRef.current = (offsetMs + currentMs) / 1000

  // Keep the face video aligned with the playhead (seek on drift).
  useEffect(() => {
    const v = videoRef.current
    if (!v || !src) return
    const target = targetSecRef.current
    const clamped = Number.isFinite(v.duration) ? Math.min(target, v.duration || target) : target
    if (Math.abs(v.currentTime - clamped) > 0.3) {
      try {
        v.currentTime = clamped
      } catch {
        /* not seekable yet */
      }
    }
  }, [currentMs, offsetMs, src])

  // Paint the first frame once the recording loads. Seeking to the playhead paints
  // it on desktop, but a paused <video> on mobile won't decode a frame from a
  // programmatic seek without a gesture — so the pip spins forever (loadeddata
  // never fires) until the user hits play. primeFirstFrame adds a muted play→pause
  // kick that mobile permits, forcing the decode + paint that fires loadeddata and
  // clears the spinner. Skipped while already playing — the play effect paints it.
  const prime = useCallback(() => {
    const v = videoRef.current
    if (!v || primedRef.current) return
    primedRef.current = true
    if (playingRef.current) return
    void primeFirstFrame(v, targetSecRef.current, () => !playingRef.current)
  }, [])

  // A hard load failure is the only case where we show nothing at all. While the
  // source is still resolving (or the video hasn't buffered its first frame yet)
  // we keep the framed rectangle in the corner and show a loading spinner inside,
  // so the preview slot is always visible rather than popping in from nothing.
  if (failed) return null
  const loading = !src || !ready

  const vertical = corner.startsWith('top') ? { top: 12 } : { bottom: 12 }
  const horizontal = corner.endsWith('right') ? { right: 12 } : { left: 12 }
  const onRight = corner.endsWith('right')

  // Dominant emotion at the current playhead, when the caller wired the curves.
  const vec = series && gridMs && labels ? vectorAt(series, gridMs, currentMs) : null
  const dom = dominant(vec)
  const caption = dom && labels && dom[1] > 0 ? { label: labels[dom[0]], color: FA_COLOR[dom[0]], pct: Math.round(dom[1] * 100) } : null

  // Fit the real aspect ratio inside a square cap per breakpoint: landscape
  // recordings fill the cap's width, portrait ones fill its height. This keeps
  // the preview faithful to how it was recorded without ever growing oversized.
  const fit = (cap: number) =>
    aspect >= 1
      ? { width: cap, height: Math.round(cap / aspect) }
      : { width: Math.round(cap * aspect), height: cap }
  const sm = fit(96)
  const lg = fit(132)

  return (
    <Box
      sx={{
        position: 'absolute',
        ...vertical,
        ...horizontal,
        // Above the stage's click-to-play scrim (zIndex 5) when interactive,
        // so the swap also works while paused.
        zIndex: onClick ? 6 : 4,
        display: 'flex',
        flexDirection: 'column',
        alignItems: onRight ? 'flex-end' : 'flex-start',
        gap: 0.5,
        pointerEvents: 'none',
      }}
    >
      <Box
        onClick={onClick}
        role={onClick ? 'button' : undefined}
        aria-label={onClick ? 'Powiększ podgląd twarzy' : undefined}
        title={onClick ? 'Powiększ podgląd twarzy' : undefined}
        sx={{
          position: 'relative',
          width: { xs: sm.width, md: lg.width },
          height: { xs: sm.height, md: lg.height },
          borderRadius: 1.5,
          overflow: 'hidden',
          border: '2px solid rgba(255,255,255,.75)',
          boxShadow: '0 6px 18px rgba(0,0,0,.45)',
          bgcolor: '#000',
          ...(onClick && {
            pointerEvents: 'auto',
            cursor: 'pointer',
            transition: 'transform .15s ease, border-color .15s ease',
            '&:hover': { transform: 'scale(1.05)', borderColor: '#fff' },
          }),
        }}
      >
        {src && (
          <Box
            component="video"
            ref={videoRef}
            src={src}
            muted
            playsInline
            preload="auto"
            onLoadedMetadata={(e) => {
              const el = e.currentTarget as HTMLVideoElement
              if (el.videoWidth > 0 && el.videoHeight > 0) {
                const a = el.videoWidth / el.videoHeight
                setAspect(a)
                onAspect?.(a)
              }
              prime()
            }}
            onLoadedData={() => setReady(true)}
            onCanPlay={() => setReady(true)}
            onError={() => setFailed(true)}
            sx={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }}
          />
        )}
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <CircularProgress size={22} thickness={4} sx={{ color: 'rgba(255,255,255,.85)' }} />
          </Box>
        )}
      </Box>

      {!loading && caption && (
        <Box
          sx={{
            maxWidth: { xs: 120, md: 160 },
            px: 0.85,
            py: 0.4,
            borderRadius: 1,
            bgcolor: 'rgba(11,12,30,.82)',
            backdropFilter: 'blur(6px)',
            boxShadow: '0 4px 12px rgba(0,0,0,.35)',
            display: 'flex',
            alignItems: 'center',
            gap: 0.6,
          }}
        >
          <Box sx={{ width: 8, height: 8, borderRadius: '2px', background: caption.color, flexShrink: 0 }} />
          <Typography
            sx={{
              color: '#fff',
              fontSize: 11,
              fontWeight: 700,
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {caption.label}{' '}
            <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', opacity: 0.9 }}>
              {caption.pct}%
            </Box>
          </Typography>
        </Box>
      )}
    </Box>
  )
}
